import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Megaphone, Plus, Edit2, Trash2, Play, Pause, 
  Eye, MousePointer, TrendingUp, Calendar, MapPin, Camera, Save, X,
  AlertTriangle, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAMPAIGN_TYPES = [
  { value: 'banner', label: 'Bannière', description: 'Affichée sur la page découverte', icon: '🖼️' },
  { value: 'offer', label: 'Offre spéciale', description: 'Avec code promo', icon: '🎁' },
  { value: 'push', label: 'Notification push', description: 'Envoyée aux utilisateurs', icon: '🔔' }
];

export default function BusinessCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef(null);

  const emptyCampaign = {
    type: 'banner',
    title: '',
    description: '',
    image: null,
    discount_percent: '',
    discount_code: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    budget_sends: 1000,
    target_cities: [],
    target_min_visits: 0,
    target_max_days_since_visit: 365
  };

  const [formData, setFormData] = useState(emptyCampaign);
  const [newCity, setNewCity] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [campaignsRes, subRes] = await Promise.all([
        fetch(`${API_URL}/api/business/campaigns`, { headers }),
        fetch(`${API_URL}/api/business/subscription`, { headers })
      ]);

      if (campaignsRes.ok) setCampaigns(await campaignsRes.json());
      if (subRes.ok) setSubscription(await subRes.json());
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const canCreateCampaigns = () => {
    if (!subscription) return false;
    return subscription.plan_details?.features?.campaigns_per_month > 0 || 
           subscription.plan_details?.features?.campaigns_per_month === -1;
  };

  const getCampaignsRemaining = () => {
    if (!subscription) return 0;
    const limit = subscription.plan_details?.features?.campaigns_per_month;
    if (limit === -1) return 'Illimité';
    if (limit === 0) return 0;
    return Math.max(0, limit - subscription.campaigns_used_this_month);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image trop grande (max 2 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const addCity = () => {
    if (newCity.trim() && !formData.target_cities.includes(newCity.trim())) {
      setFormData({ ...formData, target_cities: [...formData.target_cities, newCity.trim()] });
      setNewCity('');
    }
  };

  const removeCity = (city) => {
    setFormData({ ...formData, target_cities: formData.target_cities.filter(c => c !== city) });
  };

  const openAddDialog = () => {
    if (!canCreateCampaigns()) {
      toast.error('Passez au forfait Pro ou Premium pour créer des campagnes');
      return;
    }
    setEditingCampaign(null);
    setFormData(emptyCampaign);
    setShowDialog(true);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      type: campaign.type,
      title: campaign.title,
      description: campaign.description,
      image: campaign.image,
      discount_percent: campaign.discount_percent?.toString() || '',
      discount_code: campaign.discount_code || '',
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      budget_sends: campaign.budget_sends,
      target_cities: campaign.target_cities || [],
      target_min_visits: campaign.target_min_visits || 0,
      target_max_days_since_visit: campaign.target_max_days_since_visit || 365
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    const payload = {
      ...formData,
      discount_percent: formData.discount_percent ? parseInt(formData.discount_percent) : null
    };

    try {
      const url = editingCampaign 
        ? `${API_URL}/api/business/campaigns/${editingCampaign.id}`
        : `${API_URL}/api/business/campaigns`;
      
      const res = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }

      toast.success(editingCampaign ? 'Campagne mise à jour' : 'Campagne créée');
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (campaignId, status) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/campaigns/${campaignId}/status?status=${status}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur');
      
      toast.success(`Campagne ${status === 'active' ? 'activée' : status === 'paused' ? 'mise en pause' : 'mise à jour'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur');
      
      toast.success('Campagne supprimée');
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" asChild>
                <Link to="/business/dashboard">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
              </Button>
              <h1 className="font-semibold text-charcoal flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                Mes campagnes
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {getCampaignsRemaining()} campagne(s) restante(s) ce mois
              </span>
              <Button onClick={openAddDialog} disabled={!canCreateCampaigns()}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle campagne
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Upgrade Banner if Starter */}
        {!canCreateCampaigns() && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal mb-1">Passez au niveau supérieur</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Les campagnes publicitaires sont disponibles à partir du forfait Pro. 
                  Atteignez plus de clients et boostez vos ventes !
                </p>
                <Button asChild>
                  <Link to="/business/subscription">Voir les forfaits</Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-charcoal">{campaigns.length}</p>
            <p className="text-sm text-muted-foreground">Campagnes</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{campaigns.filter(c => c.status === 'active').length}</p>
            <p className="text-sm text-muted-foreground">Actives</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {campaigns.reduce((sum, c) => sum + (c.views_count || 0), 0)}
            </p>
            <p className="text-sm text-muted-foreground">Vues totales</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">
              {campaigns.reduce((sum, c) => sum + (c.clicks_count || 0), 0)}
            </p>
            <p className="text-sm text-muted-foreground">Clics totaux</p>
          </div>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-charcoal mb-2">Aucune campagne</h3>
            <p className="text-muted-foreground mb-4">
              Créez votre première campagne pour toucher de nouveaux clients.
            </p>
            {canCreateCampaigns() && (
              <Button onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une campagne
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="flex">
                  {/* Image */}
                  <div className="w-48 h-36 bg-gray-100 flex-shrink-0">
                    {campaign.image ? (
                      <img src={campaign.image} alt={campaign.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl">
                          {CAMPAIGN_TYPES.find(t => t.value === campaign.type)?.icon || '📢'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-charcoal">{campaign.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            campaign.status === 'active' ? 'bg-green-100 text-green-700' :
                            campaign.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                            campaign.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {campaign.status === 'active' ? 'Active' :
                             campaign.status === 'draft' ? 'Brouillon' :
                             campaign.status === 'paused' ? 'En pause' : 'Terminée'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">{campaign.description}</p>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex gap-4 text-center">
                        <div>
                          <p className="text-lg font-semibold text-charcoal">{campaign.views_count}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Vues
                          </p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-charcoal">{campaign.clicks_count}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MousePointer className="w-3 h-3" /> Clics
                          </p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold text-charcoal">
                            {campaign.views_count > 0 ? ((campaign.clicks_count / campaign.views_count) * 100).toFixed(1) : 0}%
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> CTR
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(campaign.start_date).toLocaleDateString('fr-FR')} - {new Date(campaign.end_date).toLocaleDateString('fr-FR')}
                      </span>
                      {campaign.target_cities.length > 0 && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {campaign.target_cities.join(', ')}
                        </span>
                      )}
                      {campaign.discount_percent && (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          -{campaign.discount_percent}%
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {campaign.status === 'draft' && (
                        <Button size="sm" onClick={() => updateStatus(campaign.id, 'active')}>
                          <Play className="w-3 h-3 mr-1" /> Lancer
                        </Button>
                      )}
                      {campaign.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(campaign.id, 'paused')}>
                          <Pause className="w-3 h-3 mr-1" /> Pause
                        </Button>
                      )}
                      {campaign.status === 'paused' && (
                        <Button size="sm" onClick={() => updateStatus(campaign.id, 'active')}>
                          <Play className="w-3 h-3 mr-1" /> Reprendre
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(campaign)}>
                        <Edit2 className="w-3 h-3 mr-1" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(campaign.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Modifier la campagne' : 'Nouvelle campagne'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type */}
            <div>
              <label className="text-sm font-medium mb-2 block">Type de campagne</label>
              <div className="grid grid-cols-3 gap-2">
                {CAMPAIGN_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      formData.type === type.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-primary/50'
                    }`}
                  >
                    <span className="text-2xl block mb-1">{type.icon}</span>
                    <span className="text-xs font-medium block">{type.label}</span>
                    <span className="text-xs text-muted-foreground">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Image */}
            <div>
              <label className="text-sm font-medium mb-2 block">Image de la campagne</label>
              <div 
                className="w-full h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary cursor-pointer flex items-center justify-center overflow-hidden"
                onClick={() => imageInputRef.current?.click()}
              >
                {formData.image ? (
                  <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Cliquez pour ajouter une image</p>
                  </div>
                )}
              </div>
              <input 
                ref={imageInputRef}
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleImageUpload}
              />
            </div>

            {/* Title & Description */}
            <div>
              <label className="text-sm font-medium mb-1 block">Titre *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Offre spéciale de Noël"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Décrivez votre offre..."
                className="w-full p-3 border rounded-lg resize-none h-20"
                required
              />
            </div>

            {/* Discount */}
            {formData.type === 'offer' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Réduction (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    placeholder="20"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Code promo</label>
                  <Input
                    value={formData.discount_code}
                    onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })}
                    placeholder="NOEL2025"
                  />
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Date de début *</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Date de fin *</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="text-sm font-medium mb-1 block">Budget d'envois</label>
              <Input
                type="number"
                min="100"
                value={formData.budget_sends}
                onChange={(e) => setFormData({ ...formData, budget_sends: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">Nombre maximum d'utilisateurs ciblés</p>
            </div>

            {/* Targeting */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Ciblage géographique
              </h4>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.target_cities.map((city) => (
                  <span key={city} className="inline-flex items-center gap-1 bg-white px-3 py-1 rounded-full text-sm border">
                    {city}
                    <button type="button" onClick={() => removeCity(city)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="Ajouter une ville"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                />
                <Button type="button" variant="outline" onClick={addCity}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Laissez vide pour cibler toutes les villes</p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Sauvegarde...' : (editingCampaign ? 'Mettre à jour' : 'Créer')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}